import { BottomSheetModalProvider } from "@gorhom/bottom-sheet";
import type { BaseItemSchema } from "@memorang/types";
import { SessionAutoStartContext } from "@memorang/ui/components/StudySession/SessionAutoStartContext";
import { useDevice } from "@memorang/ui/useDevice";
import { useCallback, useRef, useState } from "react";
import type { FlatList } from "react-native";
import { PassageStemModalContainer } from "../containers/PassageStemModalContainer";
import StudySessionFeedbackContainer from "../containers/StudySessionFeedbackContainer";
import StudySessionShellContainer from "../containers/StudySessionShellContainer";
import { useQuestionTipsStore } from "../hooks/useQuestionTipsStore";
import StudySessionItemsLayout from "./SessionList/StudySessionItemsLayout";

type Props = {
	sessionItems: BaseItemSchema[];
	showTimerPausingDialog?: boolean;
};

const StudySessionView = ({ sessionItems, showTimerPausingDialog }: Props) => {
	const { isDesktopWeb } = useDevice();
	const ref = useRef<FlatList<BaseItemSchema> | null>(null);
	const passageStemModalRef = useRef<{ scrollToHighlightedText: () => void }>(
		null,
	);
	const [showReadMore, setShowReadMore] = useState(false);
	// Question tips open over the item, so nothing behind them should auto-start.
	const questionTipsVisible = useQuestionTipsStore(
		(s) => s.openedVia !== undefined,
	);

	const toggleShowReadMore = useCallback(
		({ scrollToHighlight }: { scrollToHighlight?: boolean } = {}) => {
			if (scrollToHighlight) {
				passageStemModalRef.current?.scrollToHighlightedText();
			}
			setShowReadMore((prev) => !prev);
		},
		[],
	);

	return (
		<SessionAutoStartContext.Provider
			value={!(showTimerPausingDialog || questionTipsVisible)}
		>
			<BottomSheetModalProvider>
				<StudySessionShellContainer
					flashlistRef={ref}
					showStartDialog={showTimerPausingDialog}
				>
					<StudySessionItemsLayout
						ref={ref}
						items={sessionItems}
						toggleShowReadMore={toggleShowReadMore}
					/>
					{isDesktopWeb ? null : <StudySessionFeedbackContainer />}
				</StudySessionShellContainer>
				<PassageStemModalContainer
					passageStemModalRef={passageStemModalRef}
					showReadMore={showReadMore}
					propToggleShowReadMore={() => setShowReadMore(false)}
				/>
			</BottomSheetModalProvider>
		</SessionAutoStartContext.Provider>
	);
};

export default StudySessionView;
